import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'motion/react';
import { ArrowRight, Compass } from 'lucide-react';
import { EASE } from '@/shared/motion/transitions';

const PREVIEW_TILES = [
  'linear-gradient(145deg, rgba(255,248,236,0.95) 0%, #d4b87a 50%, #6b4a12 100%)',
  'linear-gradient(160deg, rgba(250,245,235,0.9) 0%, #c9a96a 45%, #4a3820 100%)',
  'linear-gradient(135deg, #f0e6d4 0%, #e0c88a 40%, #7a5c28 100%)',
  'linear-gradient(155deg, #fff9ee 0%, #d8b266 55%, #5c4218 100%)',
] as const;

export interface ExplorerLoginPromoProps {
  /** `dark` — left editorial banner; `light` — sign-in column (mobile). */
  variant?: 'dark' | 'light';
  className?: string;
}

/**
 * Public gallery CTA for the login screen.
 */
export function ExplorerLoginPromo({ variant = 'dark', className = '' }: ExplorerLoginPromoProps) {
  const reduceMotion = useReducedMotion();
  const isDark = variant === 'dark';

  if (isDark) {
    return (
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: EASE, delay: 0.2 }}
        className={`w-full ${className}`}
      >
        <Link
          to="/explore"
          className="group flex items-center gap-4 surface-card rounded-2xl px-4 py-3.5 text-ink-900 transition-all duration-300 hover:bg-white/45 focus-ring"
        >
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-gold-200/70 bg-gradient-to-br from-gold-100 to-gold-200/70 text-gold-700">
            <Compass size={20} strokeWidth={2.1} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-gold-700">
              Public gallery
            </span>
            <span className="mt-1 block truncate text-[15px] font-semibold leading-tight text-ink-900">
              Explore published studio work
            </span>
            <span className="mt-0.5 block truncate text-[12px] text-ink-500">
              No sign-in needed
            </span>
          </span>
          <span className="inline-flex h-10 shrink-0 items-center gap-2 rounded-full gold-gradient px-4 text-[12px] font-semibold text-white transition-colors">
            Browse
            <ArrowRight
              size={14}
              strokeWidth={2.5}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </span>
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: EASE, delay: 0.05 }}
      className={`w-full ${className}`}
    >
      <Link
        to="/explore"
        className={`
          group block w-full rounded-2xl overflow-hidden focus-ring transition-all duration-300
          border border-ink-200 bg-white shadow-card hover:border-gold-300/80 hover:shadow-[0_12px_36px_-10px_rgba(168,126,50,0.15)]
        `}
      >
        <div className="p-6 lg:p-7">
          <div className="flex items-start gap-4">
            <span
              className="inline-grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-gold-200/70 bg-gradient-to-br from-gold-100 to-gold-200/80 text-gold-800 lg:h-14 lg:w-14"
            >
              <Compass size={22} strokeWidth={2} />
            </span>
            <span className="flex-1 min-w-0 pt-1">
              <span
                className="block text-[11px] font-bold uppercase tracking-[0.2em] text-gold-700"
              >
                Public gallery
              </span>
              <span
                className="mt-1.5 block font-headline text-[22px] leading-[1.1] tracking-[-0.02em] text-ink-900 lg:text-[26px]"
              >
                Explore studio work
              </span>
              <span
                className="mt-2 block text-[14px] leading-relaxed text-ink-500 lg:text-[15px]"
              >
                Browse rings, renders, and videos from jewelry studios worldwide — no sign-in
                needed.
              </span>
            </span>
          </div>

          <div className="mt-6 grid grid-cols-4 gap-2.5 lg:gap-3" aria-hidden>
            {PREVIEW_TILES.map((bg, i) => (
              <span
                key={i}
                className="aspect-[3/4] min-h-[72px] rounded-xl border border-white/80 opacity-90 shadow-sm transition-opacity group-hover:opacity-100 lg:min-h-[88px]"
                style={{ background: bg }}
              />
            ))}
          </div>
        </div>

        <span
          className={`
            btn-shine flex items-center justify-center gap-2.5 w-full h-12 lg:h-[52px] text-[14px] lg:text-[15px] font-semibold gold-gradient text-white
          `}
        >
          Browse the gallery
          <ArrowRight
            size={17}
            strokeWidth={2.4}
            className="transition-transform group-hover:translate-x-0.5"
          />
        </span>
      </Link>
    </motion.div>
  );
}
